import { useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import PropTypes from "prop-types";

/**
 * A password field component integrated with react-hook-form.
 * It automatically handles form state, registration, and error display.
 * Includes a toggle to show/hide the password.
 *
 * @param {string} name - The name of the field in the form.
 * @param {object} otherProps - Other props to pass to the MUI TextField.
 * @returns {JSX.Element}
 */
const FormPasswordField = ({ name, ...otherProps }) => {
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <TextField
      {...field}
      {...otherProps}
      type={showPassword ? "text" : "password"}
      error={!!error}
      helperText={error?.message || otherProps.helperText}
      slotProps={{
        input: {
          ...otherProps.slotProps?.input,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="Passwort anzeigen/verbergen"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
                sx={{
                  "&:focus": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: "2px",
                  },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: "2px",
                  },
                }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

FormPasswordField.propTypes = {
  name: PropTypes.string.isRequired,
};

export default FormPasswordField;
